import { BillingInterval, Currency } from '../../types/subscriptions';

/**
 * Get currency symbol for display
 * @param currency - Currency code
 * @returns Currency symbol
 */
export const getCurrencySymbol = (currency: Currency | string = Currency.DZD): string => {
  const symbols: Record<string, string> = {
    [Currency.DZD]: 'DA',
    [Currency.EUR]: '\u20AC',
    [Currency.USD]: '$',
  };
  return symbols[currency] || currency;
};

/**
 * Get locale for currency formatting
 * @param currency - Currency code
 * @returns Locale string
 */
const getLocaleForCurrency = (currency: Currency | string): string => {
  const locales: Record<string, string> = {
    [Currency.DZD]: 'fr-DZ',
    [Currency.EUR]: 'fr-FR',
    [Currency.USD]: 'en-US',
  };
  return locales[currency] || 'en-US';
};

/**
 * Format price as currency
 * @param amount - Amount in currency
 * @param currency - Currency code (default: DZD)
 * @returns Formatted currency string
 */
export const formatPrice = (amount: number, currency: Currency | string = Currency.DZD): string => {
  const locale = getLocaleForCurrency(currency);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format billing interval for display
 * @param interval - Billing interval enum
 * @returns Human-readable string
 */
export const formatBillingInterval = (interval: BillingInterval): string => {
  const labels: Record<BillingInterval, string> = {
    [BillingInterval.MONTHLY]: 'per month',
    [BillingInterval.YEARLY]: 'per year',
  };
  return labels[interval];
};

/**
 * Calculate yearly equivalent price from monthly
 * @param monthlyPrice - Monthly price
 * @returns Yearly price
 */
export const calculateYearlyPrice = (monthlyPrice: number): number => {
  return monthlyPrice * 12;
};

/**
 * Calculate monthly equivalent price from yearly
 * @param yearlyPrice - Yearly price
 * @returns Monthly price
 */
export const calculateMonthlyPrice = (yearlyPrice: number): number => {
  return yearlyPrice / 12;
};

/**
 * Calculate savings percentage when billing yearly
 * @param monthlyPrice - Monthly price
 * @param yearlyPrice - Yearly price
 * @returns Percentage saved
 */
export const calculateYearlySavings = (monthlyPrice: number, yearlyPrice: number): number => {
  const yearlyEquivalent = calculateYearlyPrice(monthlyPrice);
  const savings = yearlyEquivalent - yearlyPrice;
  return (savings / yearlyEquivalent) * 100;
};

/**
 * Format resource limit for display
 * @param limit - Resource limit number or null (unlimited)
 * @returns Formatted string
 */
export const formatResourceLimit = (limit: number | null): string => {
  if (limit === null) {
    return 'Unlimited';
  }
  return limit.toLocaleString();
};
